import {caseText,entryCase,entryValue} from "./formTextCase";
import {profileAnswers,fillBlankAnswers} from "./formProfilePrefill";
import {useDisplayMode,appearanceVars} from "./formAppearance";
import UKPostalInput,{UKCountyInput} from "./UKPostalInput";
import {SlimActionButton} from "./FormActions";
import SearchableLanguageSelect from "./SearchableLanguageSelect";
// FormRenderer — mobile-first, dependency-free. Renders a HustleShield
// Forms schema in both the public /f/{slug} page AND the owner's live
// preview inside the builder.
//
// Features:
//   • mobile-first single column, 2-col on >=640px when a field says width:50
//   • phone input with country-code picker + light formatting
//   • country dropdown with flags
//   • AU suburb autocomplete (uses existing /api/wp/au/suburbs)
//   • conditional show_if logic
//   • runtime calculations for payment totals (fixed or line_items*qty + GST)
//   • payment buttons for Stripe + PayPal (both optional)
//
// Pure React, no external deps.

import ChoiceDropdown from "./ChoiceDropdown";
export {ChoiceDropdown};
import React, { useEffect, useMemo, useRef, useState } from "react";
import { presentationStyle, imagePresentationStyle, PUBLIC_FORMS_BACKEND } from "./formsApi";
import { COUNTRIES, useCountries, loadCityPostcodes, loadPostalPlaces, defaultPhoneCountry, dialFor, formatPhone, validateAuPhone, DEFAULT_COUNTRY } from "./countries";
import "./hustleshield-forms.css";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { useTranslation } from "react-i18next";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function cls(...xs) { return xs.filter(Boolean).join(" "); }

function fieldVisible(field, answers) {
  // v2: rich conditions.rules — ANY/ALL combinator with new operators.
  const c = field?.conditions;
  if (c && Array.isArray(c.rules) && c.rules.length > 0) {
    const results = c.rules.map((r) => {
      const target = (answers?.[r.field] ?? "").toString().trim().toLowerCase();
      const v = (r.value ?? "").toString().trim().toLowerCase();
      switch (r.op) {
        case "not_equals":   return target !== v;
        case "contains":     return target.includes(v);
        case "not_contains": return !target.includes(v);
        case "empty":        return target === "";
        case "filled":       return target !== "";
        case "starts_with":  return target.startsWith(v);
        case "ends_with":    return target.endsWith(v);
        case "gt":           return Number(target) > Number(v);
        case "lt":           return Number(target) < Number(v);
        default:             return target === v;
      }
    });
    return c.logic === "any" ? results.some(Boolean) : results.every(Boolean);
  }
  // Legacy fallback: single show_if rule.
  const rule = field?.show_if;
  if (!rule || !rule.field) return true;
  const target = answers?.[rule.field];
  const t = (target ?? "").toString().trim().toLowerCase();
  const v = (rule.value ?? "").toString().trim().toLowerCase();
  switch (rule.op || "equals") {
    case "not_equals": return t !== v;
    case "contains":   return t.includes(v);
    case "empty":      return t === "";
    case "filled":     return t !== "";
    default:           return t === v;
  }
}

function fieldInlineStyle(style) { return presentationStyle(style); }

export function AUSuburb({ value, onChange, onPick, onBlur, placeholder, testid }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [searchActive,setSearchActive]=useState(false);
  const ref = useRef(null);
  useEffect(()=>{if(open && items.length)ref.current?.scrollIntoView({block:"nearest"});},[open,items]);

  useEffect(() => {
    setQ(value || "");
  }, [value]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!searchActive || !q || q.length < 2) { setItems([]); setOpen(false); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        // If the query is purely digits (postcode), use the dedicated
        // /lookup endpoint — one call, no state needed.
        const digitsOnly = /^\d{3,4}$/.test(q.trim());
        if (digitsOnly) {
          const r = await fetch(`${BACKEND}/api/wp/au/lookup?postcode=${encodeURIComponent(q.trim())}`)
            .then((r) => r.ok ? r.json() : { items: [] })
            .catch(() => ({ items: [] }));
          if (cancel) return;
          setItems((r.items || []).slice(0, 20));
          setOpen((r.items || []).length > 0);
          return;
        }
        // Otherwise search by name across ALL 8 AU states in parallel.
        const states = ["QLD","NSW","VIC","WA","SA","TAS","NT","ACT"];
        const all = await Promise.all(
          states.map((st) =>
            fetch(`${BACKEND}/api/wp/au/suburbs?state=${st}&q=${encodeURIComponent(q)}&limit=10`)
              .then((r) => r.ok ? r.json() : { items: [] })
              .catch(() => ({ items: [] }))
          )
        );
        if (cancel) return;
        const merged = all.flatMap((r) => r.items || []).slice(0, 30);
        setActive(0); setItems(merged);
        setOpen(merged.length > 0);
      } catch { /* swallow */ }
    }, 180);
    return () => { cancel = true; clearTimeout(t); };
  }, [q,searchActive]);

  const pick = (it) => {
    const label = `${it.suburb} ${it.postcode} ${it.state}`;
    setSearchActive(false); setQ(label); setOpen(false); onChange?.(label);
    onPick?.(it);
  };

  return (
    <div className="hsf-sugg" ref={ref}>
      <input
        className="hsf-input"
        data-testid={testid}
        value={q}
        onChange={(e) => { setSearchActive(true); setActive(0); setQ(e.target.value); onChange?.(e.target.value); }}
        onFocus={() => { if(items.length) {setSearchActive(true);setOpen(true);} }}
        onBlur={() => { setTimeout(() => onBlur?.(), 200); /* delay so a click on the list can fire pick() first */ }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
          if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === "Enter" && items[active]) { e.preventDefault(); pick(items[active]); }
          if (e.key === "Escape")    setOpen(false);
        }}
        placeholder={placeholder || "Start typing a suburb or postcode…"}
        autoComplete="off"
        inputMode="search"
      />
      {open && items.length > 0 && (
        <div className="hsf-sugg-list">
          {items.map((it, i) => (
            <div
              key={`${it.suburb}-${it.postcode}-${it.state}`}
              className={cls("hsf-sugg-item", i === active && "active")}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              onMouseEnter={() => setActive(i)}
            >
              <strong>{it.suburb}</strong> {it.postcode} <span style={{ color: "#64748b" }}>{it.state}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// AU State select — loaded once from /api/wp/au/states (fallback to constant list).
const AU_STATES_FALLBACK = [
  { code: "ACT", name: "Australian Capital Territory" },
  { code: "NSW", name: "New South Wales" },
  { code: "NT",  name: "Northern Territory" },
  { code: "QLD", name: "Queensland" },
  { code: "SA",  name: "South Australia" },
  { code: "TAS", name: "Tasmania" },
  { code: "VIC", name: "Victoria" },
  { code: "WA",  name: "Western Australia" },
];
let _auStatesCache = null;
export function AUState({ value, onChange, testid, placeholder }) {
  const [opts, setOpts] = useState(_auStatesCache || AU_STATES_FALLBACK);
  useEffect(() => {
    if (_auStatesCache) return;
    fetch(`${BACKEND}/api/wp/au/states`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (d?.items?.length) {
          _auStatesCache = d.items.map((s) => ({ code: s.state_code, name: s.state_name }));
          setOpts(_auStatesCache);
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);
  return (
    <select className="hsf-select" value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            data-testid={testid} aria-label="Australian state">
      <option value="">{placeholder || "State"}</option>
      {opts.map((s) => (
        <option key={s.code} value={s.code}>{s.code.toUpperCase()}</option>
      ))}
    </select>
  );
}

// AU Postcode input with autocomplete — type a postcode, see every suburb
// that uses it, pick one to auto-fill siblings via onPick.
export function AUPostcode({ value, onChange, onPick, onBlur, testid, placeholder }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [searchActive,setSearchActive]=useState(false);
  const ref = useRef(null);
  useEffect(()=>{if(open && items.length)ref.current?.scrollIntoView({block:"nearest"});},[open,items]);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => {
    const d = String(q || "").replace(/[^\d]/g, "");
    if (!searchActive || d.length < 3) { setItems([]); setOpen(false); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${BACKEND}/api/wp/au/lookup?postcode=${encodeURIComponent(d)}`)
          .then((r) => r.ok ? r.json() : { items: [] })
          .catch(() => ({ items: [] }));
        if (cancel) return;
        setActive(0); setItems((r.items || []).slice(0, 25));
        setOpen((r.items || []).length > 0);
      } catch { /* swallow */ }
    }, 180);
    return () => { cancel = true; clearTimeout(t); };
  }, [q,searchActive]);
  const pickItem = (it) => {
    setSearchActive(false); setQ(it.postcode); setOpen(false);
    onChange?.(it.postcode);
    onPick?.(it);
  };
  return (
    <div className="hsf-sugg" ref={ref}>
      <input
        className="hsf-input"
        data-testid={testid}
        value={q}
        inputMode="numeric"
        autoComplete="postal-code"
        maxLength={4}
        placeholder={placeholder || "e.g. 4066"}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d]/g, "").slice(0, 4);
          setSearchActive(true); setActive(0); setQ(v); onChange?.(v);
        }}
        onFocus={() => { if(items.length) {setSearchActive(true);setOpen(true);} }}
        onBlur={() => { setTimeout(() => onBlur?.(), 200); }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
          if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === "Enter" && items[active]) { e.preventDefault(); pickItem(items[active]); }
          if (e.key === "Escape")    setOpen(false);
        }}
      />
      {open && items.length > 0 && (
        <div className="hsf-sugg-list">
          <div className="hsf-sugg-head">Pick the suburb that uses {q}:</div>
          {items.map((it, i) => (
            <div
              key={`${it.suburb}-${it.postcode}-${it.state}`}
              className={cls("hsf-sugg-item", i === active && "active")}
              onMouseDown={(e) => { e.preventDefault(); pickItem(it); }}
              onMouseEnter={() => setActive(i)}
            >
              <strong>{it.suburb}</strong>{" "}
              <span style={{ color: "#64748b" }}>{it.state}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// AU ABN input — on blur, call /api/abn/lookup (the real registry) and
// show the registered Business Name as a locked read-only sibling,
// rendering the two together as one visual "set" per spec.
export function AUAbnLookup({ value, onChange, onLookup, field, testid }) {
  const [raw, setRaw] = useState(value || "");
  const [status, setStatus] = useState("");    // "", "loading", "ok", "err"
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const lookupVersion=useRef(0);
  useEffect(() => { setRaw(value || ""); }, [value]);
  const digits = (raw || "").replace(/[^\d]/g, "");
  const display = digits.length > 2
    ? `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`.trim()
    : digits;
  const check = async () => {
    const version=++lookupVersion.current;
    if (digits.length !== 11) {
      setStatus("err"); setName(""); setMsg("ABN must be 11 digits.");
      onLookup?.({ ok: false });
      return;
    }
    setStatus("loading"); setMsg("Checking ABR…");
    try {
      const r = await fetch(`${BACKEND}/api/abn/lookup?abn=${encodeURIComponent(digits)}`)
        .then((r) => r.ok ? r.json() : null)
        .catch(() => null);
      if(version!==lookupVersion.current)return;
      if (!r) {
        setStatus("err"); setName("");
        setMsg("Couldn't reach the ABR. Please try again.");
        onLookup?.({ ok: false });
        return;
      }
      // The registry returns {status, entity_name, trading_names[], ...}.
      // Treat only `status === "verified"` as a valid, active ABN.
      const biz = r.entity_name || (Array.isArray(r.trading_names) ? r.trading_names[0] : "");
      if (r.status !== "verified" || !biz) {
        setStatus("err"); setName("");
        setMsg(
          r.status === "rejected"
            ? `ABN ${display} is not an active, registered ABN.`
            : `No active ABN found for ${display}.`
        );
        onLookup?.({ ok: false, abn: digits, status: r.status });
        return;
      }
      setStatus("ok"); setName(biz); setMsg("");
      onLookup?.({ ok: true, abn: digits, business_name: biz, raw: r });
    } catch {
      if(version!==lookupVersion.current)return;
      setStatus("err"); setMsg("Lookup failed.");
    }
  };
  return (
    <div className={cls("hsf-abn-set", status === "ok" && "hsf-abn-set-ok", status === "err" && "hsf-abn-set-err")}>
      <div className="hsf-abn-row">
        <label className="hsf-abn-mini-label">ABN</label>
        <input
          className={cls("hsf-input", status === "err" && "hsf-field-invalid")}
          data-testid={testid}
          value={display}
          onChange={(e) => {
            lookupVersion.current++;
            const d = e.target.value.replace(/[^\d]/g, "").slice(0, 11);
            setRaw(d); onChange?.(d);
            if (status) { setStatus(""); setMsg(""); setName(""); }
          }}
          onBlur={check}
          placeholder={field?.placeholder || "11 digit ABN"}
          inputMode="numeric"
          autoComplete="off"
          aria-invalid={status === "err"}
        />
      </div>
      <div className="hsf-abn-row">
        <label className="hsf-abn-mini-label">
          Business name {status === "ok" && <span className="hsf-abn-lock" title="Auto-populated from the ABR — locked">🔒</span>}
        </label>
        <input
          className={cls("hsf-input", status === "ok" && "hsf-input-locked")}
          value={name}
          readOnly
          disabled={status !== "ok"}
          placeholder={status === "loading" ? "Checking the ABR…" : "— enter an ABN above —"}
          data-testid={`${testid}-biz`}
        />
      </div>
      {status === "err" && msg && (
        <div className="hsf-err-inline" data-testid={`${testid}-err`}>{msg}</div>
      )}
    </div>
  );
}

// Email input with soft typo-warn ("Did you mean user@gmail.com?") for
// the handful of commonly fat-fingered domains. Non-blocking by design —
// users keep the right to submit their actual address even if it looks
// unusual.
const EMAIL_TYPO_FIXES = {
  "gnail.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmil.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.cm": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotnail.com": "hotmail.com",
  "hotmil.com": "hotmail.com",
  "hotmali.com": "hotmail.com",
  "hotmail.co": "hotmail.com",
  "yaho.com": "yahoo.com",
  "yhoo.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yahoo.co": "yahoo.com",
  "outllok.com": "outlook.com",
  "outook.com": "outlook.com",
  "outlok.com": "outlook.com",
  "outlook.co": "outlook.com",
  "iclould.com": "icloud.com",
  "icoud.com": "icloud.com",
  "icloud.co": "icloud.com",
};
export function EmailInput({ value, onChange, field }) {
  const [suggestion, setSuggestion] = useState("");
  const check = (v) => {
    const at = v.indexOf("@");
    if (at <= 0) { setSuggestion(""); return; }
    const dom = v.slice(at + 1).toLowerCase();
    const fix = EMAIL_TYPO_FIXES[dom];
    if (fix) setSuggestion(`${v.slice(0, at + 1)}${fix}`);
    else setSuggestion("");
  };
  const acceptFix = () => {
    if (!suggestion) return;
    onChange?.(suggestion); setSuggestion("");
  };
  return (
    <div className="hsf-email-wrap">
      <input
        className="hsf-input"
        type="email"
        inputMode="email"
        autoComplete="email"
        value={value || ""}
        placeholder={field?.placeholder || "you@example.com"}
        onChange={(e) => { onChange?.(e.target.value); setSuggestion(""); }}
        onBlur={(e) => check(e.target.value)}
        data-testid={`field-${field?.name}`}
      />
      {suggestion && (
        <div className="hsf-email-hint" role="status">
          Did you mean{" "}
          <button type="button" className="hsf-email-fix" onClick={acceptFix}
                  data-testid={`field-${field?.name}-typofix`}>
            {suggestion}
          </button>?
          <button type="button" className="hsf-email-hint-x"
                  onClick={() => setSuggestion("")} aria-label="dismiss">×</button>
        </div>
      )}
    </div>
  );
}

// Street-line type-ahead using OpenStreetMap's Nominatim API — free, no
// key, rate-limited to 1 req/sec per their policy so we debounce 450ms.
// If the API fails (offline, blocked, rate-limited) we silently fall
// back to a plain free-text input — the industry standard "manual entry
// as fallback" the owner asked for in iter252.
function StreetLineInput({ value, onChange, placeholder, testid, suburb, state, postcode }) {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);
  // Only trigger when the user has typed at least 4 chars AND has picked
  // a suburb (so we know *where* to search).
  useEffect(() => {
    if (!q || q.length < 4) { setItems([]); return; }
    if (!suburb && !postcode) { setItems([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const url = new URL("https://nominatim.openstreetmap.org/search");
        url.searchParams.set("street", q);
        if (suburb)   url.searchParams.set("city", suburb);
        if (state)    url.searchParams.set("state", state);
        if (postcode) url.searchParams.set("postalcode", postcode);
        url.searchParams.set("country", "au");
        url.searchParams.set("format", "json");
        url.searchParams.set("addressdetails", "1");
        url.searchParams.set("limit", "6");
        const r = await fetch(url.toString(), {
          headers: { "Accept-Language": "en-AU" },
        }).then((r) => r.ok ? r.json() : []).catch(() => []);
        if (cancel) return;
        setItems(Array.isArray(r) ? r : []);
        setOpen((r || []).length > 0);
      } catch { /* swallow — manual entry still works */ }
    }, 450);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, suburb, state, postcode]);
  const pick = (it) => {
    const addr = it.address || {};
    const num  = addr.house_number || "";
    const road = addr.road || addr.pedestrian || addr.residential || it.display_name?.split(",")[0] || "";
    const line = [num, road].filter(Boolean).join(" ").trim() || it.display_name;
    setQ(line); setOpen(false); onChange?.(line);
  };
  return (
    <div className="hsf-sugg" ref={ref}>
      <input
        className="hsf-input"
        data-testid={testid}
        value={q}
        autoComplete="address-line1"
        onChange={(e) => { setQ(e.target.value); onChange?.(e.target.value); }}
        onFocus={() => items.length && setOpen(true)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
          if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === "Enter" && items[active]) { e.preventDefault(); pick(items[active]); }
          if (e.key === "Escape")    setOpen(false);
        }}
        placeholder={placeholder || "e.g. 42 Example Street"}
      />
      {open && items.length > 0 && (
        <div className="hsf-sugg-list">
          <div className="hsf-sugg-head">
            Pick a matching street — or keep typing your own
          </div>
          {items.map((it, i) => {
            const addr = it.address || {};
            const line1 = [addr.house_number, addr.road || addr.pedestrian].filter(Boolean).join(" ")
                       || it.display_name.split(",")[0];
            const rest  = [addr.suburb || addr.city || addr.town, addr.state, addr.postcode].filter(Boolean).join(" ");
            return (
              <div
                key={it.place_id}
                className={cls("hsf-sugg-item", i === active && "active")}
                onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                onMouseEnter={() => setActive(i)}
              >
                <strong>{line1}</strong>
                {rest && <span style={{ color: "#64748b" }}> — {rest}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// AU Street set — just the two street lines. Address 1 is required text,
// Address 2 is the optional unit/suite/apartment info.
export function AUStreetSet({ value, onChange, testid, field }) {
  const addressText=txt=>caseText(txt,entryCase(field));
  const v = value && typeof value === "object" ? value : { line1: "", line2: "" };
  const [line1, setLine1] = useState(v.line1 || "");
  const [line2, setLine2] = useState(v.line2 || "");
  useEffect(() => { onChange?.({ line1, line2 }); /* eslint-disable-next-line */ }, [line1, line2]);
  return (
    <div className="hsf-addr-set" data-testid={testid}>
      <div className="hsf-addr-stack">
        <div className="hsf-addr-cell">
          <label className="hsf-abn-mini-label">
            Street address {field?.required && <span className="hsf-req">*</span>}
          </label>
          <StreetLineInput
            value={line1}
            onChange={txt=>setLine1(addressText(txt))}
            placeholder="e.g. 42 Example Street"
            testid={`${testid}-line1`}
          />
        </div>
        <div className="hsf-addr-cell">
          <label className="hsf-abn-mini-label">Address line 2 (optional)</label>
          <input
            className="hsf-input"
            value={line2}
            onChange={(e) => setLine2(addressText(e.target.value))}
            placeholder="e.g. Unit 7 / Level 3"
            autoComplete="address-line2"
            data-testid={`${testid}-line2`}
          />
        </div>
      </div>
    </div>
  );
}

// AU Full address — Street Set + Address Set together in one card so the
// owner only drops ONE field and the respondent sees everything they need
// to type a complete AU address: street, unit, suburb (auto), state (auto),
// postcode (auto). All three "right" inputs stay strictly validated.
export function AUFullAddressSet({ value, onChange, onValidate, testid, field }) {
  const addressCase=entryCase(field);
  const addressText=txt=>caseText(txt,addressCase);
  const v = value && typeof value === "object" ? value : {};
  const [line1,    setLine1]    = useState(v.line1    || "");
  const [line2,    setLine2]    = useState(v.line2    || "");
  const [suburb,   setSuburb]   = useState(v.suburb   || "");
  const [stateCd,  setStateCd]  = useState(v.state    || field?.default_state || "");
  const [postcode, setPostcode] = useState(v.postcode || "");
  const [subErr,   setSubErr]   = useState("");
  const [pcErr,    setPcErr]    = useState("");
  const [suburbVerified,   setSuburbVerified]   = useState(!!v.verified);
  const [postcodeVerified, setPostcodeVerified] = useState(!!v.verified);
  // Hoist current error up to parent form so submit can be blocked.
  const onValidateRef = useRef(onValidate);
  useEffect(() => { onValidateRef.current = onValidate; }, [onValidate]);
  useEffect(() => {
    const msg = subErr || pcErr;
    onValidateRef.current?.({ ok: !msg, message: msg });
  }, [subErr, pcErr]);
  useEffect(() => {
    onChange?.({
      line1, line2, suburb, state: stateCd, postcode,
      verified: suburbVerified && postcodeVerified,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line1, line2, suburb, stateCd, postcode, suburbVerified, postcodeVerified]);
  const applyPick = (it) => {
    setSuburb(addressText(it.suburb)); setStateCd(it.state); setPostcode(it.postcode);
    setSubErr(""); setPcErr("");
    setSuburbVerified(true); setPostcodeVerified(true);
  };
  const verifySuburb = async () => {
    if (!suburb) { setSuburbVerified(false); setSubErr(""); return; }
    if (suburbVerified) return;
    try {
      const q = encodeURIComponent(suburb.trim());
      const states = stateCd ? [stateCd] : ["QLD","NSW","VIC","WA","SA","TAS","NT","ACT"];
      const all = await Promise.all(states.map((s) =>
        fetch(`${BACKEND}/api/wp/au/suburbs?state=${s}&q=${q}&limit=10`)
          .then((r) => r.ok ? r.json() : { items: [] })
          .catch(() => ({ items: [] }))
      ));
      const merged = all.flatMap((r) => r.items || []);
      const match = merged.find((it) =>
        it.suburb.toUpperCase() === suburb.toUpperCase() &&
        (!postcode || String(it.postcode) === String(postcode)) &&
        (!stateCd  || it.state === stateCd)
      );
      if (match) {
        setStateCd(match.state); setPostcode(match.postcode);
        setSuburbVerified(true); setPostcodeVerified(true); setSubErr("");
      } else {
        setSuburbVerified(false);
        setSubErr("Pick a suburb from the list — this one isn't in our database.");
      }
    } catch {
      setSubErr("Couldn't verify this suburb — try again.");
    }
  };
  const verifyPostcode = async () => {
    if (!postcode) { setPostcodeVerified(false); setPcErr(""); return; }
    if (postcodeVerified) return;
    if (!/^\d{4}$/.test(postcode)) {
      setPostcodeVerified(false);
      setPcErr("Australian postcodes are 4 digits.");
      return;
    }
    try {
      const r = await fetch(`${BACKEND}/api/wp/au/lookup?postcode=${postcode}`)
        .then((r) => r.ok ? r.json() : { items: [] })
        .catch(() => ({ items: [] }));
      if (!r.items?.length) {
        setPostcodeVerified(false);
        setPcErr(`${postcode} isn't a known Australian postcode.`);
      } else {
        setPostcodeVerified(true); setPcErr("");
      }
    } catch {
      setPcErr("Couldn't verify this postcode — try again.");
    }
  };
  return (
    <div className="hsf-addr-set hsf-addr-set-full" data-testid={testid}>
      <div className="hsf-addr-stack">
        <div className="hsf-addr-cell">
          <label className="hsf-abn-mini-label">
            Street address {field?.required && <span className="hsf-req">*</span>}
          </label>
          <StreetLineInput value={line1} onChange={txt=>setLine1(addressText(txt))}
                           suburb={suburb} state={stateCd} postcode={postcode}
                           placeholder="e.g. 42 Example Street"
                           testid={`${testid}-line1`} />
        </div>
        <div className="hsf-addr-cell">
          <label className="hsf-abn-mini-label">Address line 2 (optional)</label>
          <input className="hsf-input" value={line2}
                 onChange={(e) => setLine2(addressText(e.target.value))}
                 placeholder="e.g. Unit 7 / Level 3"
                 autoComplete="address-line2"
                 data-testid={`${testid}-line2`} />
        </div>
      </div>
      <div className="hsf-addr-grid">
        <div className="hsf-addr-cell hsf-addr-cell-wide">
          <label className="hsf-abn-mini-label">
            Suburb {suburbVerified && <span className="hsf-ok-tick" title="Verified">✓</span>}
          </label>
          <AUSuburb value={suburb}
                    onChange={(txt) => { setSuburb(addressText(txt)); setSuburbVerified(false); setSubErr(""); }}
                    onPick={applyPick}
                    onBlur={verifySuburb}
                    placeholder="Start typing a suburb…"
                    testid={`${testid}-suburb`} />
          {subErr && <div className="hsf-err-inline">{subErr}</div>}
        </div>
        <div className="hsf-addr-cell">
          <label className="hsf-abn-mini-label">State {stateCd && (suburbVerified || postcodeVerified) && <span className="hsf-ok-tick" title="Verified">✓</span>}</label>
          <AUState value={stateCd}
                   onChange={(s) => { setStateCd(s); setSuburbVerified(false); setPostcodeVerified(false); }}
                   testid={`${testid}-state`} />
        </div>
        <div className="hsf-addr-cell">
          <label className="hsf-abn-mini-label">
            Postcode {postcodeVerified && <span className="hsf-ok-tick" title="Verified">✓</span>}
          </label>
          <AUPostcode value={postcode}
                      onChange={(txt) => { setPostcode(txt); setPostcodeVerified(false); setPcErr(""); }}
                      onPick={applyPick}
                      onBlur={verifyPostcode}
                      testid={`${testid}-postcode`} />
          {pcErr && <div className="hsf-err-inline">{pcErr}</div>}
        </div>
      </div>
    </div>
  );
}

// Composite AU Address field — one palette item that renders as a
// 3-piece SET: Suburb + State + Postcode, cross-linked. Picking any
// one autocompletes the others. Every value must map to a real DB row
// (strict validation); free-typed suburbs that don't match are rejected.
export function AUAddressSet({ value, onChange, onValidate, testid, field }) {
  const addressText=txt=>caseText(txt,entryCase(field));
  const v = value && typeof value === "object" ? value : { suburb: "", state: "", postcode: "" };
  const [suburb,   setSuburb]   = useState(v.suburb   || "");
  const [stateCd,  setStateCd]  = useState(v.state    || field?.default_state || "");
  const [postcode, setPostcode] = useState(v.postcode || "");
  const [subErr,   setSubErr]   = useState("");
  const [pcErr,    setPcErr]    = useState("");
  const [suburbVerified,   setSuburbVerified]   = useState(!!v.verified);
  const [postcodeVerified, setPostcodeVerified] = useState(!!v.verified);
  const onValidateRef = useRef(onValidate);
  useEffect(() => { onValidateRef.current = onValidate; }, [onValidate]);
  useEffect(() => {
    const msg = subErr || pcErr;
    onValidateRef.current?.({ ok: !msg, message: msg });
  }, [subErr, pcErr]);

  useEffect(() => {
    onChange?.({
      suburb, state: stateCd, postcode,
      verified: suburbVerified && postcodeVerified,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suburb, stateCd, postcode, suburbVerified, postcodeVerified]);

  const applyPick = (it) => {
    setSuburb(addressText(it.suburb)); setStateCd(it.state); setPostcode(it.postcode);
    setSubErr(""); setPcErr("");
    setSuburbVerified(true); setPostcodeVerified(true);
  };

  const verifySuburb = async () => {
    if (!suburb) { setSuburbVerified(false); setSubErr(""); return; }
    if (suburbVerified) return;
    try {
      const q = encodeURIComponent(suburb.trim());
      const states = stateCd ? [stateCd] : ["QLD","NSW","VIC","WA","SA","TAS","NT","ACT"];
      const all = await Promise.all(states.map((s) =>
        fetch(`${BACKEND}/api/wp/au/suburbs?state=${s}&q=${q}&limit=10`)
          .then((r) => r.ok ? r.json() : { items: [] })
          .catch(() => ({ items: [] }))
      ));
      const merged = all.flatMap((r) => r.items || []);
      const match = merged.find((it) =>
        it.suburb.toUpperCase() === suburb.toUpperCase() &&
        (!postcode || String(it.postcode) === String(postcode)) &&
        (!stateCd  || it.state === stateCd)
      );
      if (match) {
        setStateCd(match.state); setPostcode(match.postcode);
        setSuburbVerified(true); setPostcodeVerified(true); setSubErr("");
      } else {
        setSuburbVerified(false);
        setSubErr("Pick a suburb from the list — this one isn't in our database.");
      }
    } catch {
      setSubErr("Couldn't verify this suburb — try again.");
    }
  };
  const verifyPostcode = async () => {
    if (!postcode) { setPostcodeVerified(false); setPcErr(""); return; }
    if (postcodeVerified) return;
    if (!/^\d{4}$/.test(postcode)) {
      setPostcodeVerified(false);
      setPcErr("Australian postcodes are 4 digits.");
      return;
    }
    try {
      const r = await fetch(`${BACKEND}/api/wp/au/lookup?postcode=${postcode}`)
        .then((r) => r.ok ? r.json() : { items: [] })
        .catch(() => ({ items: [] }));
      if (!r.items?.length) {
        setPostcodeVerified(false);
        setPcErr(`${postcode} isn't a known Australian postcode.`);
      } else {
        setPostcodeVerified(true); setPcErr("");
      }
    } catch {
      setPcErr("Couldn't verify this postcode — try again.");
    }
  };

  return (
    <div className="hsf-addr-set" data-testid={testid}>
      <div className="hsf-addr-grid">
        <div className="hsf-addr-cell hsf-addr-cell-wide">
          <label className="hsf-abn-mini-label">
            Suburb {suburbVerified && <span className="hsf-ok-tick" title="Verified">✓</span>}
          </label>
          <AUSuburb
            value={suburb}
            onChange={(txt) => { setSuburb(addressText(txt)); setSuburbVerified(false); setSubErr(""); }}
            onPick={applyPick}
            onBlur={verifySuburb}
            placeholder="Start typing a suburb…"
            testid={`${testid}-suburb`}
          />
          {subErr && <div className="hsf-err-inline">{subErr}</div>}
        </div>
        <div className="hsf-addr-cell">
          <label className="hsf-abn-mini-label">State {stateCd && (suburbVerified || postcodeVerified) && <span className="hsf-ok-tick" title="Verified">✓</span>}</label>
          <AUState
            value={stateCd}
            onChange={(s) => { setStateCd(s); setSuburbVerified(false); setPostcodeVerified(false); }}
            testid={`${testid}-state`}
          />
        </div>
        <div className="hsf-addr-cell">
          <label className="hsf-abn-mini-label">
            Postcode {postcodeVerified && <span className="hsf-ok-tick" title="Verified">✓</span>}
          </label>
          <AUPostcode
            value={postcode}
            onChange={(txt) => { setPostcode(txt); setPostcodeVerified(false); setPcErr(""); }}
            onPick={applyPick}
            onBlur={verifyPostcode}
            testid={`${testid}-postcode`}
          />
          {pcErr && <div className="hsf-err-inline">{pcErr}</div>}
        </div>
      </div>
    </div>
  );
}

// US city/state can have many ZIPs: offer choices rather than guessing.
function CountryPostcodeInput({value,country,city,region,onChange,onPick,required}) {
  const [codes,setCodes]=useState([]);
  const [places,setPlaces]=useState([]),[lookupNote,setLookupNote]=useState("");
  useEffect(()=>{
    setPlaces([]);setLookupNote("");
    if(country!=="US" || !/^\d{5}$/.test(String(value||"").trim()))return;
    let cancelled=false;
    const timer=setTimeout(async()=>{
      try {
        const rows=await loadPostalPlaces(country,value);
        if(cancelled)return;
        setPlaces(rows);
        if(rows.length===1)onPick?.(rows[0]);
        if(!rows.length)setLookupNote("No match in our US ZIP lookup. Check the country/code, or enter your city and state manually.");
      } catch {if(!cancelled)setLookupNote("ZIP lookup is temporarily unavailable. Please enter your city and state manually.");}
    },450);
    return()=>{cancelled=true;clearTimeout(timer);};
  },[country,value]);
  useEffect(()=>{
    setCodes([]);
    if(country!=="US" || !city || !region)return;
    const controller=new AbortController();
    (async()=>{
      try {
        const rows=await loadCityPostcodes(country,city,region);
        if(controller.signal.aborted)return;
        setCodes(rows);
        if(rows.length===1 && !value)onChange(rows[0]);
      } catch { /* manual ZIP entry remains available */ }
    })();
    return()=>controller.abort();
  },[country,city,region]);
  if(country==="GB")return <UKPostalInput postcode value={value} required={required} onChange={onChange} onPick={onPick}/>;
  return <div>
    <input className="hsf-input" data-testid="field-postcode" autoComplete="postal-code" required={required} value={value||""} onChange={e=>onChange(e.target.value)}/>
    {lookupNote && <div className="hsf-help" role="status">{lookupNote}</div>}
    {places.length>1 && <select className="hsf-select" aria-label="Cities for ZIP code" defaultValue="" onChange={e=>onPick?.(places[Number(e.target.value)])}>
      <option value="" disabled>Choose a city for {value}…</option>
      {places.map((item,i)=><option key={i} value={i}>{item.city}, {item.region}</option>)}
    </select>}
    {codes.length>1 && <select className="hsf-select" aria-label="Available ZIP codes" value={codes.includes(value)?value:""} onChange={e=>onChange(e.target.value)}>
      <option value="">Choose a ZIP code for {city}…</option>
      {codes.map(code=><option key={code} value={code}>{code}</option>)}
    </select>}
  </div>;
}

// Country-filtered city/suburb suggestions; manual entry remains available.
function CountryCityInput({value,country,onChange,onPick}) {
  const [items,setItems]=useState([]),[open,setOpen]=useState(false),[active,setActive]=useState(0);
  const [typed,setTyped]=useState(false);
  const [lookupNote,setLookupNote]=useState("");
  useEffect(()=>{setItems([]);setOpen(false);setTyped(false);},[country]);
  useEffect(()=>{
    if(country==="AU" || !typed || String(value||"").length<3){setItems([]);return;}
    const controller=new AbortController();
    const timer=setTimeout(async()=>{
      try {
        let rows;
        setLookupNote("");
        if(country==="US" && /^\d{5}$/.test(value.trim())) {
          rows=await loadPostalPlaces(country,value);
          if(controller.signal.aborted)return;
          setItems(rows);setOpen(true);setActive(0);
          if(!rows.length)setLookupNote("No match in our US ZIP lookup. Check the country/code, or enter the city manually.");
          return;
        }
        const params=new URLSearchParams({q:value,countrycode:country,limit:"6"});
        params.append("layer","city");params.append("layer","district");params.append("layer","locality");
        const r=await fetch("https://photon.komoot.io/api/?"+params,{signal:controller.signal});
        const data=await r.json();
        rows=(data.features||[]).map(x=>x.properties).filter(x=>x.countrycode?.toUpperCase()===country).map(x=>({city:x.name,region:x.state,postcode:x.postcode}));
        rows=rows.filter(x=>x.city);
        if(country==="US") {
          const expanded=await Promise.all(rows.map(async item=>{
            const codes=await loadCityPostcodes(country,item.city,item.region);
            return codes.length ? codes.map(postcode=>({...item,postcode})) : [item];
          }));
          rows=expanded.flat();
        }
        if(controller.signal.aborted)return;
        const seen=new Set();
        setItems(rows.filter(item=>{const key=[item.city,item.region,item.postcode].join("|");if(seen.has(key))return false;seen.add(key);return true;}));
        setActive(0);setOpen(true);
      } catch {if(!controller.signal.aborted)setItems([]);}
    },650);
    return()=>{clearTimeout(timer);controller.abort();};
  },[value,country,typed]);
  const pick=item=>{onPick(item);setOpen(false);setTyped(false);};
  if(country==="GB")return <UKPostalInput value={value} onChange={onChange} onPick={onPick}/>;
  if(country==="AU")return <AUSuburb value={value} onChange={onChange} onPick={item=>onPick({city:item.suburb,region:item.state,postcode:item.postcode})}/>;
  return <div className="hsf-sugg">
    <input className="hsf-input" value={value||""} autoComplete="address-level2" aria-label="City / suburb"
      onChange={e=>{setTyped(true);onChange(e.target.value);}}
      onBlur={()=>setOpen(false)} onFocus={()=>setOpen(true)}
      onKeyDown={e=>{
        if(!open || !items.length)return;
        if(e.key==="ArrowDown"){e.preventDefault();setActive(x=>Math.min(x+1,items.length-1));}
        if(e.key==="ArrowUp"){e.preventDefault();setActive(x=>Math.max(0,x-1));}
        if(e.key==="Enter"){e.preventDefault();pick(items[active]);}
        if(e.key==="Escape")setOpen(false);
      }}/>
    {lookupNote && <div className="hsf-help" role="status">{lookupNote}</div>}
    {open && items.length>0 && <div className="hsf-sugg-list" role="listbox" aria-label="City / suburb suggestions">
      {items.map((item,i)=><button type="button" key={i} className="hsf-sugg-item" role="option" aria-selected={i===active}
        onMouseDown={e=>e.preventDefault()} onClick={()=>pick(item)}>{[item.city,item.region,item.postcode].filter(Boolean).join(", ")}</button>)}
      {country!=="AU" && <small>© OpenStreetMap contributors{country==="US" ? " · ZIP data: Zippopotam.us / GeoNames" : ""}</small>}
    </div>}
  </div>;
}

export function PhoneInput({ value, onChange, field, onValidate, onCountryChange }) {
  const countries=useCountries().filter(c=>c.dial);
  const preferredCountry=field?.country || DEFAULT_COUNTRY;
  const initialCountry=preferredCountry==="auto" || String(value||"").startsWith("+")?defaultPhoneCountry(value,preferredCountry==="auto"?DEFAULT_COUNTRY:preferredCountry):preferredCountry;
  const [country, setCountry] = useState(initialCountry);
  // When the field is pinned (mobile_phone / landline_phone) we force the
  // formatter and validator to that kind no matter what the digits say.
  const kind       = field?.validation?.au_phone_kind     || "any";
  const stateRestr = field?.validation?.au_state_restrict || "";
  const forced     = (kind === "mobile" || kind === "landline") ? kind : undefined;
  // Store raw formatted local; prefix dial code only on emit.
  const [local, setLocal] = useState(() => {
    const dial = dialFor(initialCountry);
    let raw = String(value || "").trim();
    if (raw.startsWith(dial)) raw = raw.slice(dial.length).trim();
    return formatPhone(raw.replace(/[^\d]/g, ""), initialCountry, { kind: forced });
  });
  const [err, setErr] = useState("");       // validation message
  const [showPop, setShowPop] = useState(false);
  const [editing, setEditing] = useState(false);
  const [touched, setTouched] = useState(false);

  // Sync from parent while avoiding the feedback loop that caused random
  // digits to pile up (iter249 bug): strip our own dial prefix before
  // reformatting, and skip the update if the normalised value already
  // matches what we're showing locally.
  useEffect(() => {
    const dial = dialFor(country);
    let raw = String(value || "").trim();
    if (raw.startsWith(dial)) raw = raw.slice(dial.length).trim();
    const f = formatPhone(raw.replace(/[^\d]/g, ""), country, { kind: forced });
    if (f !== local) setLocal(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, country, forced]);

  // Validate locally even when the network is unavailable. The server repeats
  // the same checks at submission; country changes remain international-aware.
  const onValidateRef = useRef(onValidate);
  useEffect(() => { onValidateRef.current = onValidate; }, [onValidate]);
  useEffect(() => {
    const result=country==="AU"?validateAuPhone(local,{kind,state:stateRestr}):{ok:true,message:""};
    // Partial numbers are expected while typing. Keep submit validation active,
    // but show the correction only after leaving the field.
    setErr(touched && !editing ? result.message || "" : "");
    setShowPop(touched && !editing && !result.ok);
    onValidateRef.current?.(result);
  }, [local,country,kind,stateRestr,editing,touched]);

  return (
    <div className={cls("hsf-phone-wrap", err && "hsf-field-invalid")}>
      {showPop && err && (
        <div className="hsf-err-pop" role="alert" data-testid={`field-${field?.name}-err-pop`}>
          <strong>Fix this:</strong> {err}
          <button type="button" className="hsf-err-pop-x"
                  onClick={() => setShowPop(false)} aria-label="dismiss">×</button>
        </div>
      )}
      <select
        className="hsf-select"
        value={country}
        onChange={(e) => {
          const code=e.target.value;
          setCountry(code);
          const formatted=formatPhone(local,code,{kind:forced});
          setLocal(formatted);
          onChange?.(formatted ? `${dialFor(code)} ${formatted}` : "");
          onCountryChange?.(code);
        }}
        aria-label="Country code"
      >
        {countries.map((c) => (
          <option key={c.code} value={c.code}>{c.flag} {c.dial} · {c.name}</option>
        ))}
      </select>
      <input
        className="hsf-input"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={local}
        placeholder={
          field?.placeholder
          || (country !== "AU" ? ({US:"(201) 555-0123",CA:"(204) 555-0123",GB:"07700 900123",NZ:"021 123 4567",PL:"512 345 678"}[country] || "Phone number") : kind === "landline" ? (({NSW:"02",ACT:"02",VIC:"03",TAS:"03",QLD:"07",SA:"08",WA:"08",NT:"08"}[field?.default_state] || "02")+" 9999 9999") : kind === "mobile" ? "0412 345 678" : "0412 345 678")
        }
        onChange={(e) => {
          const f = formatPhone(e.target.value, country, { kind: forced });
          setLocal(f);
          onChange?.(f ? `${dialFor(country)} ${f}`.trim() : "");
        }}
        onFocus={() => {setEditing(true);setShowPop(false);}}
        onBlur={() => {setTouched(true);setEditing(false);}}
        data-testid={`field-${field?.name}`}
        aria-invalid={!!err}
        ref={node=>{if(node)node.setCustomValidity(err || "");}}
      />

    </div>
  );
}

function Rating({ value, onChange, max = 5, testid }) {
  const v = Number(value || 0);
  return (
    <div className="hsf-rating" data-testid={testid}>
      {Array.from({ length: max }).map((_, i) => (
        <button
          type="button"
          key={i}
          className={cls(i < v && "active")}
          onClick={() => onChange?.(i + 1)}
          aria-label={`Rate ${i + 1}`}
        >★</button>
      ))}
    </div>
  );
}

function iso2Date(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toISOString().slice(0, 10);
  } catch { return ""; }
}

function AppointmentPicker({ form, field, value, onChange }) {
  // `value` here is the normalised {start,end,label,timezone} object (or empty)
  const cfg = field.appointment || {};
  const slug = form?.slug;
  const [day, setDay] = useState(() => iso2Date(value?.start) || "");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tzLabel, setTzLabel] = useState(cfg.timezone || "UTC");

  const minDay = useMemo(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  }, []);
  const maxDay = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + Number(cfg.booking_window_days || 30));
    return d.toISOString().slice(0, 10);
  }, [cfg.booking_window_days]);

  useEffect(() => {
    if (!day || !slug) { setSlots([]); return; }
    let cancel = false;
    setLoading(true); setErr("");
    (async () => {
      try {
        const r = await fetch(
          `${PUBLIC_FORMS_BACKEND}/api/forms/public/${slug}/appointment/slots?start_date=${day}&field_id=${field.id || ""}`,
          { credentials: "omit" },
        );
        if (!r.ok) throw new Error(await r.text());
        const data = await r.json();
        if (cancel) return;
        setTzLabel(data.timezone || cfg.timezone || "UTC");
        setSlots((data.days?.[0]?.slots) || []);
      } catch (e) {
        if (!cancel) { setErr("Could not load times for that day."); setSlots([]); }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [day, slug, field.id]);  // eslint-disable-line

  const pick = (slot) => {
    onChange({
      start: slot.start, end: slot.end, label: slot.label, timezone: tzLabel,
    });
  };

  return (
    <div className="hsf-appt">
      <input
        type="date"
        className="hsf-input"
        value={day}
        min={minDay}
        max={maxDay}
        onChange={(e) => { setDay(e.target.value); onChange(null); }}
        data-testid={`field-${field.name}-date`}
      />
      {day && (
        <div className="hsf-appt-tz">
          Times shown in <strong>{tzLabel}</strong>
          {cfg.location ? ` · ${cfg.location}` : ""}
        </div>
      )}
      {loading && <div className="hsf-appt-loading">Loading times…</div>}
      {err && <div className="hsf-err" style={{ marginTop: 10 }}>{err}</div>}
      {!loading && day && slots.length === 0 && !err && (
        <div className="hsf-appt-empty">No times available on this day.</div>
      )}
      {slots.length > 0 && (
        <div
          className="hsf-appt-slots"
          data-testid={`field-${field.name}-slots`}
        >
          {slots.map((s) => (
            <button
              type="button"
              key={s.start}
              disabled={!s.available}
              className={cls(
                "hsf-appt-slot",
                value?.start === s.start && "active",
                !s.available && "disabled",
              )}
              onClick={() => pick(s)}
              data-testid={`slot-${s.start}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      {value?.start && (
        <div className="hsf-appt-chosen">
          Selected: <strong>{value.label}</strong>
        </div>
      )}
    </div>
  );
}

// Auto-fill AU-address sibling fields that share the same `pair_key`.
// Picking a suburb (or postcode) on one field automatically populates
// the matching au_suburb / au_state / au_postcode siblings so the user
// never has to retype what the DB already knows.
function fillAUSiblings(form, field, onChange, picked) {
  const key = field?.pair_key;
  if (!key || !form?.schema?.fields) return;
  for (const f of form.schema.fields) {
    if (f.id === field.id || f.pair_key !== key) continue;
    if (f.type === "au_suburb"   && picked.suburb)   onChange(f.name, picked.suburb);
    if (f.type === "au_state"    && picked.state)    onChange(f.name, picked.state);
    if (f.type === "au_postcode" && picked.postcode) onChange(f.name, picked.postcode);
  }
}

function Field({ form, field, value, answers, onChange, onFieldValidation, labelAddon, onAction, actionsForField }) {
  const countries=useCountries();
  field={...field,country:form.schema.settings?.default_country || field.country,default_state:form.schema.settings?.default_state || field.default_state};
  if (!fieldVisible(field, answers)) return null;
  const t = field.type || "text";
  if (field.parent_id) {
    const parent=(form.schema.fields || []).find(f=>f.id===field.parent_id);
    if(parent && !fieldVisible(parent,answers)) return null;
  }
  if(t==="block") return <fieldset className="hsf-full" style={presentationStyle(field.style)}>
    {field.label && <legend>{field.label}</legend>}
    <div className="hsf-row" style={{gap:field.style?.gap ?? 16}}>
      {(form.schema.fields || []).filter(f=>f.parent_id===field.id).map(f=><Field key={f.id} form={form} field={f} value={answers[f.name]} answers={answers} onChange={onChange} onFieldValidation={onFieldValidation} labelAddon={labelAddon} onAction={onAction} actionsForField={actionsForField}/>)}
    </div></fieldset>;
  const widthClass = (field.width === 50) ? "" : "hsf-full";
  const inlineStyle = fieldInlineStyle(field.style);
  const common = {
    "data-testid": `field-${field.name}`,
    style:{...Object.fromEntries(Object.entries(inlineStyle || {}).filter(([k])=>["fontSize","fontFamily","fontWeight","fontStyle","textDecoration","color"].includes(k))),...(field.input_chars?{width:"calc("+field.input_chars+"ch + 26px)",maxWidth:"100%",minWidth:0}:{})},
    value: (t === "checkbox" || t === "appointment") ? undefined : (value ?? ""),
    onChange: (e) => {
      const node=e.target,start=node.selectionStart,end=node.selectionEnd;
      const composing=!!e.nativeEvent?.isComposing;
      onChange(field.name,node.value,{composing});
      if(!composing && start!==null && end!==null && ["text","textarea"].includes(t))
        requestAnimationFrame(()=>{if(document.activeElement===node)node.setSelectionRange(Math.min(start,node.value.length),Math.min(end,node.value.length));});
    },
    onCompositionEnd: (e) => onChange(field.name,e.target.value),
    autoCapitalize: entryCase(field,form.schema.settings)==="upper"?"characters":entryCase(field,form.schema.settings)==="proper"?"words":"off",
    placeholder: field.placeholder || "",
    maxLength: field.max_length || field.maxlength || undefined,
    pattern: field.pattern || undefined,
    required: !!field.required,
    readOnly: !!field.read_only,
  };

  if (t === "heading") {
    return (
      <div className="hsf-full">
        <div className="hsf-heading" style={inlineStyle}>{(field.label || "").replaceAll("{Business name}",form.schema.settings?.business_name || "Your business name")}</div>
      </div>
    );
  }
  if (t === "divider") {
    return <div className="hsf-full"><div className="hsf-divider" /></div>;
  }
  if (t === "spacer") {
    return <div className="hsf-full"><div style={{ height: field.size || 24 }} /></div>;
  }
  if (t === "rich_text") {
    return (
      <div className="hsf-full">
        <div className="hsf-richtext" style={inlineStyle}>
          {field.html || field.label || ""}
        </div>
      </div>
    );
  }
  if (t === "image") {
    const align = field.style?.align || field.align || "left";
    if (!field.src) return null;
    return (
      <div className="hsf-full" style={{ textAlign: align }}>
        <img src={field.src} alt={field.alt || ""}
             style={imagePresentationStyle(field)} />
      </div>
    );
  }

  const fieldActions=actionsForField?.(field) || field.inline_actions || [];
  const labelActions=fieldActions.filter(a=>a.button_placement==="label");
  const inputActions=fieldActions.filter(a=>a.button_placement!=="label");
  if(t==="action_button")return <div className={cls("hsf-field",widthClass)} style={inlineStyle}><SlimActionButton definition={field} onAction={onAction}/></div>;
  const wrap = (inner) => (
    <div className={cls("hsf-field", widthClass)} style={inlineStyle}>
      {field.label && t !== "checkbox" && (
        <label className="hsf-label" style={Object.fromEntries(Object.entries(inlineStyle || {}).filter(([k])=>["fontSize","fontFamily","fontWeight","fontStyle","textDecoration","color"].includes(k)))}>
          {answers.country==="GB" && field.name==="region" ? "County / region (optional)" : answers.country==="GB" && field.name==="city" ? "Town / city" : field.label}
          {field.required && !(answers.country==="GB" && field.name==="region") && <span className="hsf-req">*</span>}{labelAddon?.(field)}{labelActions.map((a,i)=><SlimActionButton key={a.id||i} definition={a} onAction={onAction}/>)}
        </label>
      )}
      {inputActions.length?<div className="hsf-input-actions">{inner}{inputActions.map((a,i)=><SlimActionButton key={a.id||i} definition={a} onAction={onAction}/>)}</div>:inner}
      {field.help_text && <div className="hsf-help">{field.help_text}</div>}
    </div>
  );

  if(answers.country==="GB" && field.name==="region")return wrap(<UKCountyInput value={value} onChange={v=>onChange(field.name,v)}/>);
  if(field.name==="postcode" && form.schema.fields.some(f=>f.name==="country" && f.type==="country")) {
    return wrap(<CountryPostcodeInput value={value} country={answers.country} city={answers.city} region={answers.region} required={field.required} onChange={v=>onChange(field.name,v)} onPick={item=>{onChange("city",item.city);onChange("region",item.region);if(item.postcode)onChange("postcode",item.postcode);}}/>);
  }
  if(field.name==="city" && form.schema.fields.some(f=>f.name==="country" && f.type==="country")) {
    return wrap(<CountryCityInput value={value} country={answers.country || "US"}
      onChange={v=>onChange(field.name,v)} onPick={item=>{
        onChange(field.name,item.city);
        if(item.region || answers.country==="GB")onChange("region",item.region || "");
        if(item.postcode)onChange("postcode",item.postcode);
      }} />);
  }
  switch (t) {
    case "textarea":
      return wrap(<textarea className="hsf-textarea" {...common} rows={4} />);
    case "number":
      return wrap(<input className="hsf-input" type="number" inputMode="decimal" {...common} />);
    case "email":
      return wrap(
        <EmailInput value={value} field={field}
                    onChange={(v) => onChange(field.name, v)} />
      );
    case "url":
      return wrap(<input className="hsf-input" type="url" inputMode="url" {...common} />);
    case "date":
      return wrap(<input className="hsf-input" type="date" {...common} />);
    case "phone":
      return wrap(
        <PhoneInput field={{...field,country:answers.country || field.country}} value={value}
                    onCountryChange={code=>{if(form.schema.fields.some(f=>f.name==="country" && f.type==="country"))onChange("country",code);}}
                    onChange={(v) => onChange(field.name, v)}
                    onValidate={(r) => onFieldValidation?.(field.name, r)} />
      );
    case "mobile_phone":
      return wrap(
        <PhoneInput
          field={{ ...field, country: field.country || "AU",
                   validation: { ...(field.validation || {}), au_phone_kind: "mobile" } }}
          value={value}
          onChange={(v) => onChange(field.name, v)}
          onValidate={(r) => onFieldValidation?.(field.name, r)}
        />
      );
    case "landline_phone":
      return wrap(
        <PhoneInput
          field={{ ...field, country: field.country || "AU",
                   validation: { ...(field.validation || {}), au_phone_kind: "landline" } }}
          value={value}
          onChange={(v) => onChange(field.name, v)}
          onValidate={(r) => onFieldValidation?.(field.name, r)}
        />
      );
    case "country":
      return wrap(
        <select className="hsf-select" {...common} multiple={!!field.multiple} value={field.multiple?(Array.isArray(value)?value:[]):(value || "")} onChange={e=>onChange(field.name,field.multiple?Array.from(e.target.selectedOptions,o=>o.value):e.target.value)}>
          
          {!field.multiple&&<option value="">Select…</option>}
          {countries.map((c) => (
            <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
          ))}
        </select>
      );
    case "au_suburb":
      return wrap(
        <AUSuburb value={value} onChange={(v) => onChange(field.name, v)}
                  onPick={(it) => fillAUSiblings(form, field, onChange, {
                    suburb: it.suburb, state: it.state, postcode: it.postcode,
                  })}
                  placeholder={field.placeholder} testid={`field-${field.name}`} />
      );
    case "au_state":
      return wrap(
        <AUState value={value} onChange={(v) => onChange(field.name, v)}
                 placeholder={field.placeholder} testid={`field-${field.name}`} />
      );
    case "au_postcode":
      return wrap(
        <AUPostcode value={value} onChange={(v) => onChange(field.name, v)}
                    onPick={(it) => fillAUSiblings(form, field, onChange, {
                      suburb: it.suburb, state: it.state, postcode: it.postcode,
                    })}
                    placeholder={field.placeholder} testid={`field-${field.name}`} />
      );
    case "au_abn":
      return wrap(
        <AUAbnLookup value={value} field={field}
                     onChange={(v) => {onChange(field.name,v);onChange(field.name+"__business_name","");}}
                     onLookup={(r) => {
                       if (!(r && r.ok && r.business_name))onChange(field.name+"__business_name","");
                       if (r && r.ok && r.business_name) {
                         onChange(`${field.name}__business_name`, r.business_name);
                       }
                       // Also report to the form so submit is blocked on invalid ABNs.
                       onFieldValidation?.(field.name, {
                         ok: !!(r && r.ok),
                         message: r && !r.ok ? "ABN is not registered/active." : "",
                       });
                     }}
                     testid={`field-${field.name}`} />
      );
    case "au_address":
      return wrap(
        <AUAddressSet value={value} field={{...field,text_case:entryCase(field,form.schema.settings)}}
                      onChange={(v) => onChange(field.name, v)}
                      onValidate={(r) => onFieldValidation?.(field.name, r)}
                      testid={`field-${field.name}`} />
      );
    case "au_street":
      return wrap(
        <AUStreetSet value={value} field={{...field,text_case:entryCase(field,form.schema.settings)}}
                     onChange={(v) => onChange(field.name, v)}
                     testid={`field-${field.name}`} />
      );
    case "au_full_address":
    case "au_postal_address":
      return wrap(
        <AUFullAddressSet value={value} field={{...field,text_case:entryCase(field,form.schema.settings)}}
                          onChange={(v) => onChange(field.name, v)}
                          onValidate={(r) => onFieldValidation?.(field.name, r)}
                          testid={`field-${field.name}`} />
      );
    case "select":
      if(field.multiple)return wrap(<ChoiceDropdown field={field} value={value} onChange={v=>onChange(field.name,v)}/>);
      return wrap(
        <select className="hsf-select" {...common} multiple={!!field.multiple} value={field.multiple?(Array.isArray(value)?value:[]):(value || "")} onChange={e=>onChange(field.name,field.multiple?Array.from(e.target.selectedOptions,o=>o.value):e.target.value)}>
          
          {!field.multiple&&<option value="">Select…</option>}
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    case "radio":
      return wrap(
        <div className="hsf-check-list">
          {(field.options || []).map((opt) => (
            <label key={opt} className="hsf-check-row">
              <input
                type="radio" name={field.name}
                checked={value === opt}
                onChange={() => onChange(field.name, opt)}
                data-testid={`field-${field.name}-${opt}`}
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    case "checkbox": {
      // multi-check: value is array of strings
      const arr = Array.isArray(value) ? value : [];
      return wrap(
        <>
          <div className="hsf-label" style={{ marginBottom: 10 }}>
            {field.label}{field.required && <span className="hsf-req">*</span>}{labelAddon?.(field)}{labelActions.map((a,i)=><SlimActionButton key={a.id||i} definition={a} onAction={onAction}/>)}
          </div>
          <div className="hsf-check-list">
            {(field.options || []).map((opt) => (
              <label key={opt} className="hsf-check-row">
                <input
                  type="checkbox"
                  checked={arr.includes(opt)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...arr, opt]
                      : arr.filter((x) => x !== opt);
                    onChange(field.name, next);
                  }}
                  data-testid={`field-${field.name}-${opt}`}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </>
      );
    }
    case "rating":
      return wrap(
        <Rating value={value} onChange={(v) => onChange(field.name, v)}
                testid={`field-${field.name}`} />
      );
    case "appointment":
      return wrap(
        <AppointmentPicker
          form={form}
          field={field}
          value={value}
          onChange={(v) => onChange(field.name, v)}
        />
      );
    default:
      return wrap(<input className="hsf-input" type="text" autoComplete="off" {...common} />);
  }
}

function computeTotal(payment, answers) {
  const mode = payment?.mode || "fixed";
  if (mode === "fixed") return Number(payment?.amount || 0);
  if (mode === "customer_amount") return Math.round(Number(answers?.[payment?.total_field] || 0)*100)/100;
  if (mode === "line_items") {
    let sub = 0;
    for (const it of (payment?.line_items || [])) {
      const q = it.qty_field ? Number(answers?.[it.qty_field] || 0) : 1;
      sub += Math.max(q, 0) * Number(it.price || 0);
    }
    const gst = Number(payment?.gst_percent || 0);
    return gst > 0 ? +(sub * (1 + gst / 100)).toFixed(2) : +sub.toFixed(2);
  }
  return 0;
}

export default function FormRenderer({ form, preview = false, onSubmitted, sourceUrl = "", screenLanguageOverride = "", answerPatch=null, onAnswersChange, labelAddon, submitOverride, onFormAction, formIntro=null, beforeSubmit=null, autoCheckoutProvider=null }) {
  const schema = form?.schema || {};
  const theme = schema.theme || {};
  const settings = schema.settings || {};
  const payment = schema.payment || {};
  const paypalPub = schema.paypal_public || {};
  const formType = schema.form_type || "one_page";
  const steps = schema.steps || [{ id: "default", label: "Form" }];
  const [stepIdx, setStepIdx] = useState(0);
  const [resetRevision,setResetRevision]=useState(0);
  const formRef=useRef(null);
  useEffect(()=>{if(window.parent!==window)window.parent.postMessage({hpBlocks:true,slug:form.slug,requestTheme:true},"*");},[form.slug]);
  const touchedFields=useRef(new Set());
  const [answers, setAnswers] = useState(()=>Object.fromEntries((schema.fields || []).filter(field=>field.default_value!==undefined).map(field=>[field.name,field.default_value])));
  useEffect(()=>{
    if(preview || settings.prefill_profile===false)return;
    const controller=new AbortController();
    fetch("/api/forms/profile-prefill",{credentials:"include",cache:"no-store",signal:controller.signal})
     .then(r=>r.ok?r.json():null).then(data=>{
       if(data?.profile && !controller.signal.aborted)setAnswers(old=>{
        const fields=schema.fields||[];
        const visible=fields.filter(field=>{
          const parent=fields.find(p=>p.id===field.parent_id);
          return ["text","email","phone","mobile_phone","landline_phone","country"].includes(field.type)&&fieldVisible(field,old)&&(!parent||fieldVisible(parent,old));
        });
        return fillBlankAnswers(old,profileAnswers(data.profile,visible),touchedFields.current);
       });
     }).catch(()=>{});
    return()=>controller.abort();
  },[form.id,preview,settings.prefill_profile]);
  useEffect(()=>{if(answerPatch)setAnswers(a=>({...a,...answerPatch}));},[answerPatch]);
  useEffect(()=>{onAnswersChange?.(answers);},[answers,onAnswersChange]);
  const { i18n } = useTranslation();
  const screenLanguage = screenLanguageOverride || i18n.resolvedLanguage || i18n.language || document.documentElement.lang || "en";
  const [receiptLanguage, setReceiptLanguage] = useState(screenLanguage);
  const [sendReceipt, setSendReceipt] = useState(true);
  const [receiptLanguageChosen, setReceiptLanguageChosen] = useState(false);
  useEffect(() => {
    if (!receiptLanguageChosen) setReceiptLanguage(screenLanguage);
  }, [screenLanguage, receiptLanguageChosen]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);     // {thankyou_message, thankyou_url, submission_id, payment:bool}
  const [err, setErr] = useState("");

  const displayMode=useDisplayMode();
  const styleVars=useMemo(()=>appearanceVars(theme,displayMode),[theme,displayMode]);

  const setField = (name, val, meta={}) => {
    const definition=(schema.fields||[]).find(f=>f.name===name);
    if(definition && !meta.composing)val=entryValue(definition,val,settings);
    touchedFields.current.add(name);
    setAnswers((a) => ({ ...a, [name]: val }));
  };

  // Per-field validation state hoisted up from PhoneInput etc. so submit
  // can refuse to proceed when any field reports an error. Keyed by
  // field name → "" when valid, error message when invalid.
  const [fieldErrors, setFieldErrors] = useState({});
  const onFieldValidation = (name, r) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (!r || r.ok) delete next[name]; else next[name] = r.message || "Invalid value";
      return next;
    });
  };

  const actionsForField=field=>[...(field.inline_actions || []),...(schema.fields || []).filter(f=>f.type==="action_button" && f.button_placement!=="row" && f.target_field===field.name && fieldVisible(f,answers))];
  const handleAction=async(definition,event)=>{
    const action=definition.action || "reset_form";
    if(action==="reset_form"){
      setAnswers(Object.fromEntries((schema.fields || []).filter(f=>f.default_value!==undefined).map(f=>[f.name,f.default_value])));
      setFieldErrors({});setErr("");setDone(null);setStepIdx(0);setResetRevision(v=>v+1);onFormAction?.(definition,event);return;
    }
    if(action==="clear_field"){setField(definition.target_field,"");return;}
    if(action==="scroll_to_field"){formRef.current?.querySelector('[data-testid="field-'+definition.target_field+'"]')?.scrollIntoView({behavior:"smooth",block:"center"});return;}
    if(action==="go_to_top"){formRef.current?.scrollIntoView({behavior:"smooth",block:"start"});return;}
    if(action==="print_form"){window.print();return;}
    if(action==="copy_link"){await navigator.clipboard.writeText(location.origin+location.pathname);return;}
    if(action==="open_url"){const url=definition.action_url || "";if(/^https:\/\//.test(url)||(/^\/(?!\/)/.test(url)))window.location.assign(url);return;}
    if(action==="submit_form"){formRef.current?.requestSubmit();return;}
    if(onFormAction?.(definition,event)!==true)setErr("This action needs a connected module.");
  };
  const total = computeTotal(payment, answers);
  const paymentEnabled = settings.enable_payment && total > 0;
  const providers = payment.providers || ["stripe"];

  const submit = async (e) => {
    e?.preventDefault?.();
    if (preview) return;
    const mismatch=(schema.fields || []).find(f=>f.validation?.equals_field && fieldVisible(f,answers) && String(answers[f.name] || "").trim().toLowerCase()!==String(answers[f.validation.equals_field] || "").trim().toLowerCase());
    if(mismatch){setErr("Email addresses must match.");return;}
    // Block submit when any field reports a live validation error (phone,
    // ABN, address). The errors already render inline next to each field.
    const names = Object.keys(fieldErrors).filter(k=>{
      const field=(schema.fields || []).find(f=>f.name===k);
      const parent=field?.parent_id && (schema.fields || []).find(f=>f.id===field.parent_id);
      return !!fieldErrors[k] && field && fieldVisible(field,answers) && (!parent || fieldVisible(parent,answers));
    });
    if (names.length) {
      setErr(
        names.length === 1
          ? `Please fix the error on "${names[0]}" before submitting.`
          : `Please fix the ${names.length} highlighted fields before submitting.`
      );
      // Scroll the first invalid field into view so users see where to fix it.
      setTimeout(() => {
        const first = document.querySelector(`[data-testid="field-${names[0]}"]`);
        first?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      }, 10);
      return;
    }
    if(submitOverride){
      const node=formRef.current;
      if(node && !node.checkValidity()){node.reportValidity();return;}
      setSubmitting(true);setErr("");
      try{await submitOverride(answers);}catch(error){setErr(error.message||"Unable to continue.");}finally{setSubmitting(false);}
      return;
    }
    setErr(""); setSubmitting(true);
    // Pull the honeypot value straight off the <form> element so a bot
    // that fills every field reveals itself. Humans never see it. We
    // scope the lookup to this form only — never grab a different
    // form's input if the page contains more than one.
    let hpVal = "";
    try {
      const formEl = e?.target?.closest?.("form") || e?.target;
      const el = formEl?.elements?.hp_website
             || formEl?.querySelector?.('input[name="hp_website"]');
      hpVal = (el && el.value) || "";
    } catch (_) { /* best effort */ }
    try {
      // Flatten composite address/street fields into flat columns so
      // Zapier / GHL / exports see  <name>_line1, <name>_line2, <name>_suburb,
      // <name>_state, <name>_postcode  instead of a nested object. Primary
      // key stays with the original composite so forms that read it as a
      // single value still work (iter252).
      const flattenComposite = (ans) => {
        const out = { ...ans };
        for (const f of (form?.schema?.fields || [])) {
          if (!["au_street","au_address","au_full_address","au_postal_address"].includes(f.type)) continue;
          const v = ans[f.name];
          if (!v || typeof v !== "object") continue;
          const map = {
            line1:    v.line1    || "",
            line2:    v.line2    || "",
            suburb:   v.suburb   || "",
            state:    v.state    || "",
            postcode: v.postcode || "",
          };
          for (const [k, val] of Object.entries(map)) {
            if (val !== "" && val != null) out[`${f.name}_${k}`] = val;
          }
          // Also expose a one-line display string for notification emails.
          const oneLine = [map.line1, map.line2, map.suburb, map.state, map.postcode]
            .filter(Boolean).join(", ");
          if (oneLine) out[f.name] = oneLine;
        }
        return out;
      };
      const flatAnswers = flattenComposite(answers);
      const res = await fetch(
        `${PUBLIC_FORMS_BACKEND}/api/forms/public/${form.slug}/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            answers: flatAnswers,
            source_url: sourceUrl,
            screen_language: screenLanguage,
            receipt_language: receiptLanguage,
            send_receipt: sendReceipt,
            ts_token: form.ts_token || "",
            render_version_token: form.render_version_token || "",
            hp_website: hpVal,
            turnstile_token:
              (window.turnstile && window.__hsf_ts_token) || "",
          }),
        },
      );
      // IMPORTANT: HustleShield silent-drops bots with 200/{ok:true} — so
      // every response that comes back here is treated as "accepted" from
      // the user's point of view. Real bot verdicts are logged server-side.
      const data = await res.json().catch(() => { throw new Error("The service did not return a result. Please try again."); });
      if (!res.ok) {
        // Appointment booking conflict / validation — surface to user.
        setErr(typeof data.detail === "string" ? data.detail : "Please try again.");
        return;
      }
      if(data.payment && autoCheckoutProvider==="eway"){
        await payWithStripe("eway",data.submission_id);
        return;
      }
      setDone({
        submission_id: data.submission_id,
        thankyou_message: data.thankyou_message || settings.thankyou_message,
        thankyou_url: data.thankyou_url || settings.thankyou_url,
        payment: !!data.payment,
        appointment: data.appointment || null,
      });
      onSubmitted?.(data, flatAnswers);
    } catch (e) {
      setErr("Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const payWithStripe = async (provider = "stripe", submissionId = done?.submission_id) => {
    try {
      const res = await fetch(
        `${PUBLIC_FORMS_BACKEND}/api/forms/public/${form.slug}/checkout`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            answers,
            submission_id: submissionId || null,
            provider:typeof provider==="string"?provider:"stripe",
            origin_url: window.location.origin + window.location.pathname,
          }),
        },
      );
      const data = await res.json();
      if (data.url) {
        if(provider==="eway" && window.top!==window) window.top.location.href=data.url;
        else window.location.href=data.url;
      }
      else setErr(data.detail || "Could not start checkout");
    } catch { setErr("The payment service could not be reached. Please try again."); }
  };

  const payWithPayPal = async () => {
    try {
      const resp = await fetch(
        `${PUBLIC_FORMS_BACKEND}/api/forms/public/${form.slug}/paypal/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            answers, submission_id: done?.submission_id || null,
          }),
        },
      );
      const data = await resp.json();
      if (!data.order_id) { setErr(data.detail || "PayPal failed"); return; }
      // Lightweight approve-via-redirect dance (keeps us dep-free).
      const base = paypalPub.mode === "live"
        ? "https://www.paypal.com/checkoutnow"
        : "https://www.sandbox.paypal.com/checkoutnow";
      const returnUrl = `${window.location.origin}${window.location.pathname}?pp_capture=${data.order_id}&slug=${form.slug}`;
      window.location.href = `${base}?token=${data.order_id}&returnUrl=${encodeURIComponent(returnUrl)}`;
    } catch { setErr("PayPal is not configured for this form yet."); }
  };

  // auto-capture on return from PayPal
  useEffect(() => {
    const url = new URL(window.location.href);
    const cap = url.searchParams.get("pp_capture");
    const slug = url.searchParams.get("slug");
    if (!cap || slug !== form.slug) return;
    (async () => {
      try {
        const r = await fetch(
          `${PUBLIC_FORMS_BACKEND}/api/forms/public/${form.slug}/paypal/capture`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ order_id: cap }),
          },
        );
        const d = await r.json();
        setDone((prev) => ({
          ...(prev || {}),
          thankyou_message: d.paid
            ? "Payment received — thank you!"
            : "Payment incomplete.",
          payment: false,
        }));
      } catch { /* ignore */ }
    })();
  }, [form.slug]);

  if (done && !done.payment) {
    // Final thank-you after both submit AND (optional) payment.
    const appt = done.appointment;
    // Only auto-redirect when there's no appointment to add; otherwise
    // users must have a chance to click Add-to-Calendar first.
    if (done.thankyou_url && !appt) {
      // Delay a tick so React paints the ✓ before leaving.
      setTimeout(() => { window.location.href = done.thankyou_url; }, 400);
    }
    return (
      <div className="hsf-root" data-hsf-mode={displayMode} data-hsf-palette={theme.palette_colours?"true":"false"} data-hsf-season={theme.seasonal_background||""} style={styleVars}>
        {settings.custom_css && (
          <style dangerouslySetInnerHTML={{ __html: settings.custom_css }} />
        )}
        <div className="hsf-shell">
          <div className="hsf-card">
            <div className="hsf-done" data-testid="form-thankyou">
              <div className="hsf-done-check">✓</div>
              <h2 style={{ margin: "0 0 8px" }}>Thanks!</h2>
              <p style={{ color: "#64748b", margin: 0 }}>
                {done.thankyou_message || "We'll be in touch shortly."}
              </p>
            </div>

            {appt && appt.calendar && (
              <div className="hsf-cal-box" data-testid="add-to-calendar">
                <div className="hsf-cal-title">Your appointment</div>
                <div className="hsf-cal-when">
                  {new Date(appt.start).toLocaleString(undefined, {
                    weekday: "long", day: "numeric", month: "long",
                    year: "numeric", hour: "numeric", minute: "2-digit",
                  })}
                  {" "}({appt.timezone})
                </div>
                <div className="hsf-cal-btns">
                  <a className="hsf-cal-btn hsf-cal-google"
                     href={appt.calendar.google}
                     target="_blank" rel="noreferrer"
                     data-testid="cal-google">
                    Add to Google
                  </a>
                  <a className="hsf-cal-btn hsf-cal-outlook"
                     href={appt.calendar.outlook}
                     target="_blank" rel="noreferrer"
                     data-testid="cal-outlook">
                    Outlook
                  </a>
                  <a className="hsf-cal-btn hsf-cal-apple"
                     href={`${BACKEND}${appt.calendar.ics_path}?sub_id=${encodeURIComponent(done.submission_id)}`}
                     data-testid="cal-apple">
                    Apple / .ics
                  </a>
                </div>
              </div>
            )}

            {appt && done.thankyou_url && (
              <a className="hsf-cal-continue"
                 href={done.thankyou_url}
                 data-testid="cal-continue">
                Continue →
              </a>
            )}
          </div>
          {settings.show_footer!==false && <div className="hsf-brand-footer">{settings.footer_text ?? "Secured by HustleShield"}</div>}
        </div>
      </div>
    );
  }

  if (done && done.payment) {
    return (
      <div className="hsf-root" data-hsf-mode={displayMode} data-hsf-palette={theme.palette_colours?"true":"false"} data-hsf-season={theme.seasonal_background||""} style={styleVars}>
        <div className="hsf-shell">
          <div className="hsf-card">
            <h1 className="hsf-title">Last step — payment</h1>
            <p className="hsf-sub">Your submission was received. Pay below to finalise.</p>
            <div className="hsf-pay-summary">
              <div className="hsf-pay-row"><span>Subtotal</span><span>{(payment.currency || "AUD")} {total.toFixed(2)}</span></div>
              <div className="hsf-pay-total hsf-pay-row">
                <span>Total due</span><span>{(payment.currency || "AUD")} {total.toFixed(2)}</span>
              </div>
            </div>
            {err && <div className="hsf-err">{err}</div>}
            <div className="hsf-pay-btns">
              {providers.includes("stripe") && (
                <button type="button" className="hsf-btn-stripe"
                        onClick={payWithStripe}
                        data-testid="pay-stripe-button">
                  Pay with Card (Stripe)
                </button>
              )}
              {providers.includes("eway") && <button type="button" className="hsf-btn-stripe" onClick={()=>payWithStripe("eway")}>Pay with Card (eWAY)</button>}
              {providers.includes("paypal") && paypalPub.enabled && (
                <button type="button" className="hsf-btn-paypal"
                        onClick={payWithPayPal}
                        data-testid="pay-paypal-button">
                  Pay with PayPal
                </button>
              )}
            </div>
          </div>
          {settings.show_footer!==false && <div className="hsf-brand-footer">{settings.footer_text ?? "Secured by HustleShield"}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="hsf-root" data-hsf-mode={displayMode} data-hsf-palette={theme.palette_colours?"true":"false"} data-hsf-season={theme.seasonal_background||""} style={styleVars} data-testid="hsf-public-form">
      {settings.custom_css && (
        <style dangerouslySetInnerHTML={{ __html: settings.custom_css }} />
      )}
      <div className="hsf-shell">
        <form ref={formRef} className="hsf-card" style={presentationStyle(settings.card_style)} onSubmit={submit} noValidate>
          <header style={presentationStyle(settings.header_style)}>
            {settings.header_logo && <img src={settings.header_logo} alt="" style={{maxWidth:"100%",width:120,height:"auto"}} />}
            {settings.business_name && settings.show_business_name!==false && <div>{settings.business_name}</div>}
            {settings.show_subtitle !== false && settings.subtitle_position === "above" && settings.subtitle && <p className="hsf-sub">{settings.subtitle}</p>}
            {settings.show_title !== false && <h1 className="hsf-title">{form.name || "Contact us"}</h1>}
            {settings.show_subtitle !== false && settings.subtitle_position !== "above" && settings.subtitle && <p className="hsf-sub">{settings.subtitle}</p>}
          </header>
          {formIntro}

          {formType === "staged" && steps.length > 1 && (
            <ol className="hsf-progress" data-testid="hsf-progress">
              {steps.map((s, i) => (
                <li key={s.id}
                    className={cls(
                      "hsf-progress-item",
                      i === stepIdx && "active",
                      i < stepIdx && "done",
                    )}
                    data-testid={`hsf-progress-${i}`}>
                  <span className="hsf-progress-num">{i < stepIdx ? "✓" : i + 1}</span>
                  <span className="hsf-progress-label">{s.label}</span>
                </li>
              ))}
            </ol>
          )}

          {err && <div className="hsf-err">{err}</div>}

          {/* honeypot — must stay off-screen; read via the form itself */}
          <input
            type="text" name="hp_website" tabIndex={-1}
            data-testid="hsf-honeypot"
            style={{ position: "absolute", left: "-9999px", top: 0 }}
            autoComplete="off"
          />

          <div className="hsf-row" style={{gap:settings.layout_gap ?? 16}}>
            {(schema.fields || [])
              .filter(f=>!f.parent_id && !(f.type==="action_button" && f.button_placement && f.button_placement!=="row"))
              .filter((f) => formType !== "staged"
                || (f.step_id || "default") === (steps[stepIdx]?.id || "default"))
              .map((f) => (
                <Field key={(f.id || f.name)+":"+(answers._account_loaded_version || "")+":"+resetRevision}
                       onAction={handleAction}
                       actionsForField={actionsForField}
                       labelAddon={labelAddon}
                       form={form}
                       field={f}
                       value={answers[f.name]}
                       answers={answers}
                       onChange={setField}
                       onFieldValidation={onFieldValidation} />
              ))}
          </div>

          {paymentEnabled && (
            <div className="hsf-pay-summary" data-testid="payment-summary">
              {(payment.line_items || []).map((it) => {
                const q = it.qty_field ? Number(answers?.[it.qty_field] || 0) : 1;
                const line = Math.max(q, 0) * Number(it.price || 0);
                return (
                  <div key={it.label} className="hsf-pay-row">
                    <span>{it.label}{it.qty_field ? ` × ${q}` : ""}</span>
                    <span>{payment.currency} {line.toFixed(2)}</span>
                  </div>
                );
              })}
              {Number(payment.gst_percent) > 0 && payment.mode === "line_items" && (
                <div className="hsf-pay-row">
                  <span>GST {payment.gst_percent}%</span>
                  <span>incl.</span>
                </div>
              )}
              <div className="hsf-pay-total hsf-pay-row">
                <span>Total</span>
                <span>{payment.currency || "AUD"} {total.toFixed(2)}</span>
              </div>
            </div>
          )}

          {(schema.fields || []).some((field) => field.type === "email") && (
            <fieldset className="hsf-email-language">
              <legend>Email confirmation</legend>
              <label><input type="checkbox" checked={sendReceipt} onChange={(event) => setSendReceipt(event.target.checked)} /> Send a receipt in my selected language</label>
              {sendReceipt && <label>Receipt language
                <SearchableLanguageSelect aria-label="Receipt language" value={receiptLanguage} onChange={event=>{setReceiptLanguage(event.target.value);setReceiptLanguageChosen(true)}}/>
              </label>}
            </fieldset>
          )}

          {beforeSubmit}

          {formType === "staged" && steps.length > 1 ? (
            <div className="hsf-step-nav">
              {stepIdx > 0 && (
                <button type="button" className="hsf-step-back"
                        onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                        data-testid="step-back">
                  ← Back
                </button>
              )}
              {stepIdx < steps.length - 1 ? (
                <button type="button" className="hsf-submit"
                  style={presentationStyle(settings.button_style)}
                        onClick={() => setStepIdx((i) => Math.min(steps.length - 1, i + 1))}
                        data-testid="step-next">
                  Next →
                </button>
              ) : (
                <button
                  type="submit"
                  className="hsf-submit"
                  style={presentationStyle(settings.button_style)}
                  disabled={submitting || preview}
                  data-testid="form-submit-button"
                >
                  {submitting ? "Sending…" : (settings.submit_label || "Submit")}
                </button>
              )}
            </div>
          ) : (
            <button
              type="submit"
              className="hsf-submit"
                  style={presentationStyle(settings.button_style)}
              disabled={submitting || preview}
              data-testid="form-submit-button"
            >
              {submitting
                ? "Sending…"
                : (settings.submit_label || "Submit")}
            </button>
          )}

          {settings.show_footer !== false && <div className="hsf-shield" data-testid="shield-badge">
            {settings.footer_text ?? "Secured by HustleShield"}
          </div>}
        </form>
      </div>
    </div>
  );
}
